import { z } from 'zod'

import { ErrorCodeSchema } from './errors.js'
import {
  BanDtoSchema,
  CommandDtoSchema,
  DetectionDtoSchema,
  PlayerDtoSchema,
  ScreenshotDtoSchema,
  ServerStatusSchema,
} from './panel.js'

/**
 * SPEC.md §4.3 / docs/API.md §21 — the `/v1/ws` protocol.
 *
 * One socket per browser tab. The handshake carries the panel access JWT either
 * as `?token=<jwt>` (what the browser `WebSocket` constructor can do) or as
 * `Sec-WebSocket-Protocol: bearer, <jwt>`; a rejected handshake is an HTTP `401`
 * with the `ApiError` envelope, never a close code.
 *
 * Two rules the panel depends on (API.md §21.3, PANEL.md §11):
 * - Every event payload is the **same DTO the REST API returns**, so a WS event
 *   can be written straight into the TanStack Query cache. The DTOs are imported
 *   from `panel.ts`; nothing is re-declared here.
 * - An unknown `type` must be **ignored, never thrown** — the union may grow
 *   server-side ahead of the panel. Parse with `safeParse` and drop failures.
 *
 * The hub is fire-and-forget in-process pub/sub: nothing is replayed, so after
 * every `hello` the client re-subscribes and backfills over REST (API.md §21.5).
 */

const isoDateTime = z.iso.datetime()
const uuid = z.uuid()

// ───────────────────────── Client → server (API.md §21.2) ─────────────────────

/** Max server ids in one `subscribe`/`unsubscribe`. There is no implicit "all servers" subscription. */
export const WS_MAX_SUBSCRIBE_IDS = 100

const serverIds = z.array(uuid).min(1).max(WS_MAX_SUBSCRIBE_IDS)

/** `{ op:'subscribe', serverIds }` — the hub filters the list against `server_members`. */
export const WsSubscribeMessageSchema = z.strictObject({
  op: z.literal('subscribe'),
  serverIds,
})

/** `{ op:'unsubscribe', serverIds }` — answered with a `subscribed` message carrying the remaining set. */
export const WsUnsubscribeMessageSchema = z.strictObject({
  op: z.literal('unsubscribe'),
  serverIds,
})

/**
 * `{ op:'auth', token }` — re-verifies a freshly refreshed access token in place
 * so a proactive refresh does not have to tear the socket down. Answered with a
 * new `hello`; an invalid token produces an `error` (`AUTH_TOKEN_INVALID`)
 * followed by close `4401`.
 */
export const WsAuthMessageSchema = z.strictObject({
  op: z.literal('auth'),
  token: z.string().min(16).max(4096),
})

/**
 * `{ op:'ping' }` — sent every 25 s because browsers cannot emit raw ping
 * frames. The server answers `pong`; no `pong` within 10 s means the socket is
 * dead and the client reconnects.
 */
export const WsPingMessageSchema = z.strictObject({ op: z.literal('ping') })

export const WsClientMessageSchema = z.discriminatedUnion('op', [
  WsSubscribeMessageSchema,
  WsUnsubscribeMessageSchema,
  WsAuthMessageSchema,
  WsPingMessageSchema,
])
export type WsClientMessage = z.infer<typeof WsClientMessageSchema>

export type WsSubscribeMessage = z.infer<typeof WsSubscribeMessageSchema>
export type WsUnsubscribeMessage = z.infer<typeof WsUnsubscribeMessageSchema>
export type WsAuthMessage = z.infer<typeof WsAuthMessageSchema>
export type WsPingMessage = z.infer<typeof WsPingMessageSchema>

// ───────────────────────── Server → client (API.md §21.3) ─────────────────────
// Every message carries `type` and `ts`; all except `hello`, `subscribed`,
// `pong` and `error` also carry `serverId` and `data`.

/** Sent on connect and again after a successful in-place `auth`. Triggers re-subscribe + REST backfill. */
export const WsHelloMessageSchema = z.object({
  type: z.literal('hello'),
  ts: isoDateTime,
  data: z.object({ userId: uuid, serverTime: isoDateTime }),
})

/** Confirms the authorised subset after `subscribe`/`unsubscribe`; updates the `subscriptions` slice only. */
export const WsSubscribedMessageSchema = z.object({
  type: z.literal('subscribed'),
  ts: isoDateTime,
  data: z.object({ serverIds: z.array(uuid).max(WS_MAX_SUBSCRIBE_IDS) }),
})

/** A new detection row. PANEL.md §11: pushed to the feed and prepended into page 0 of the detections list. */
export const WsDetectionMessageSchema = z.object({
  type: z.literal('detection'),
  serverId: uuid,
  ts: isoDateTime,
  data: DetectionDtoSchema,
})

/** A new ban row. PANEL.md §11: prepended into the active bans list, bumps the player's `banCount`. */
export const WsBanMessageSchema = z.object({
  type: z.literal('ban'),
  serverId: uuid,
  ts: isoDateTime,
  data: BanDtoSchema,
})

/**
 * A revoked ban. Deliberately not a full `BanDto`: PANEL.md §11 only patches
 * `active:false` + `revokedBy` onto the cached rows, and `revokedBy` here is the
 * revoking staff member's display name, not their user id.
 */
export const WsUnbanMessageSchema = z.object({
  type: z.literal('unban'),
  serverId: uuid,
  ts: isoDateTime,
  data: z.object({
    banId: uuid,
    banCode: z.string().regex(/^SAC-[0-9A-Z]{8}$/),
    revokedBy: z.string().max(80),
  }),
})

/**
 * Emitted by `jobs/offline-detector.ts` and by heartbeat processing. PANEL.md
 * §11 merges exactly these fields into `qk.health(serverId)` and patches the
 * matching card in `qk.servers()`.
 */
export const WsServerStatusMessageSchema = z.object({
  type: z.literal('server.status'),
  serverId: uuid,
  ts: isoDateTime,
  data: z.object({
    status: ServerStatusSchema,
    playersOnline: z.number().int().min(0).max(4096),
    lastHeartbeatAt: isoDateTime.nullable(),
    agentVersion: z.string().max(32).nullable(),
    agentConfigVersion: z.number().int().min(0).max(1_000_000),
  }),
})

/** A player connected. PANEL.md §11: prepended into the online page, sets `online:true` on cached rows. */
export const WsPlayerJoinMessageSchema = z.object({
  type: z.literal('player.join'),
  serverId: uuid,
  ts: isoDateTime,
  data: PlayerDtoSchema,
})

/** A player disconnected. `playerSource` is the in-game id (never `source`); `reason` is the drop reason. */
export const WsPlayerLeaveMessageSchema = z.object({
  type: z.literal('player.leave'),
  serverId: uuid,
  ts: isoDateTime,
  data: z.object({
    playerId: uuid,
    playerSource: z.number().int().min(1).max(4096),
    reason: z.string().max(500),
  }),
})

/** A command changed state (`delivered`/`acked`/`failed`/`expired`); PANEL.md §11 patches the row by `id`. */
export const WsCommandUpdatedMessageSchema = z.object({
  type: z.literal('command.updated'),
  serverId: uuid,
  ts: isoDateTime,
  data: CommandDtoSchema,
})

/** Fires on `uploaded` **and** on `failed`/`expired`, so the drawer can swap the thumbnail for an error state. */
export const WsScreenshotUpdatedMessageSchema = z.object({
  type: z.literal('screenshot.updated'),
  serverId: uuid,
  ts: isoDateTime,
  data: ScreenshotDtoSchema,
})

/**
 * A new config version landed. Only the version and the author's display name
 * travel — PANEL.md §11 invalidates `qk.config`/`qk.configVersions`/`qk.health`
 * rather than patching, unless the ConfigPage draft is dirty.
 */
export const WsConfigUpdatedMessageSchema = z.object({
  type: z.literal('config.updated'),
  serverId: uuid,
  ts: isoDateTime,
  data: z.object({
    version: z.number().int().min(1).max(1_000_000),
    changedBy: z.string().max(80),
  }),
})

/** Answer to `{ op:'ping' }`. Resets the client liveness timer; carries no `data`. */
export const WsPongMessageSchema = z.object({
  type: z.literal('pong'),
  ts: isoDateTime,
})

/**
 * A per-message failure that does not close the socket — e.g. a `subscribe` for
 * a server the caller has no membership on (`FORBIDDEN`, that id is dropped from
 * the subscription set), or an invalid in-place `auth` (`AUTH_TOKEN_INVALID`,
 * followed by close `4401`).
 */
export const WsErrorMessageSchema = z.object({
  type: z.literal('error'),
  ts: isoDateTime,
  data: z.object({ code: ErrorCodeSchema, message: z.string().max(500) }),
})

/**
 * The live map's snapshot: every online player's place in the world.
 *
 * A whole-roster snapshot rather than a diff, because it is small, it is sent
 * every few seconds anyway, and a diff stream has to be reconciled by a client
 * that may have missed a frame. Nothing here is persisted on either side.
 */
export const WsPositionsMessageSchema = z.object({
  type: z.literal('positions'),
  serverId: uuid,
  ts: isoDateTime,
  data: z.object({
    players: z
      .array(
        z.object({
          playerSource: z.number().int().min(1).max(4096),
          name: z.string().max(128),
          x: z.number(),
          y: z.number(),
          z: z.number(),
          heading: z.number(),
          inVehicle: z.boolean(),
          health: z.number().int(),
        }),
      )
      .max(512),
  }),
})

export const WsServerMessageSchema = z.discriminatedUnion('type', [
  WsHelloMessageSchema,
  WsSubscribedMessageSchema,
  WsDetectionMessageSchema,
  WsBanMessageSchema,
  WsUnbanMessageSchema,
  WsServerStatusMessageSchema,
  WsPlayerJoinMessageSchema,
  WsPlayerLeaveMessageSchema,
  WsCommandUpdatedMessageSchema,
  WsScreenshotUpdatedMessageSchema,
  WsConfigUpdatedMessageSchema,
  WsPositionsMessageSchema,
  WsPongMessageSchema,
  WsErrorMessageSchema,
])
export type WsServerMessage = z.infer<typeof WsServerMessageSchema>

export type WsHelloMessage = z.infer<typeof WsHelloMessageSchema>
export type WsSubscribedMessage = z.infer<typeof WsSubscribedMessageSchema>
export type WsDetectionMessage = z.infer<typeof WsDetectionMessageSchema>
export type WsBanMessage = z.infer<typeof WsBanMessageSchema>
export type WsUnbanMessage = z.infer<typeof WsUnbanMessageSchema>
export type WsServerStatusMessage = z.infer<typeof WsServerStatusMessageSchema>
export type WsPlayerJoinMessage = z.infer<typeof WsPlayerJoinMessageSchema>
export type WsPlayerLeaveMessage = z.infer<typeof WsPlayerLeaveMessageSchema>
export type WsCommandUpdatedMessage = z.infer<typeof WsCommandUpdatedMessageSchema>
export type WsScreenshotUpdatedMessage = z.infer<typeof WsScreenshotUpdatedMessageSchema>
export type WsConfigUpdatedMessage = z.infer<typeof WsConfigUpdatedMessageSchema>
export type WsPositionsMessage = z.infer<typeof WsPositionsMessageSchema>
export type WsPongMessage = z.infer<typeof WsPongMessageSchema>
export type WsErrorMessage = z.infer<typeof WsErrorMessageSchema>

/** Every `type` the server may send today. Used by the panel's exhaustiveness test. */
export const WS_SERVER_EVENT_TYPES = [
  'hello',
  'subscribed',
  'detection',
  'ban',
  'unban',
  'server.status',
  'player.join',
  'player.leave',
  'command.updated',
  'screenshot.updated',
  'config.updated',
  'positions',
  'pong',
  'error',
] as const
export type WsServerEventType = (typeof WS_SERVER_EVENT_TYPES)[number]

// ──────────────────────────── Close codes (API.md §21.6) ──────────────────────

/**
 * The close codes `/v1/ws` uses. Handshake failures are HTTP `401`/`404`, not
 * close codes — there is no code for a rejected upgrade.
 */
export const WS_CLOSE_CODES = {
  /** Normal closure (navigation, explicit `close()`). No reconnect. */
  NORMAL: 1000,
  /** Server going away — graceful shutdown, or two consecutive missed pongs. Reconnect with backoff. */
  GOING_AWAY: 1001,
  /** Policy violation — > 20 msgs/s, frame > 64 KiB, malformed JSON, or a 6th socket. Back off 30 s. */
  POLICY_VIOLATION: 1008,
  /** Unhandled server error. Reconnect with backoff. */
  INTERNAL_ERROR: 1011,
  /** Access token expired or invalidated (`session_version` bumped). Refresh, then reconnect; log out if the refresh fails. */
  TOKEN_EXPIRED: 4401,
} as const satisfies Record<string, number>

export type WsCloseCodeName = keyof typeof WS_CLOSE_CODES
export type WsCloseCode = (typeof WS_CLOSE_CODES)[WsCloseCodeName]

/** Socket and reconnect limits from API.md §21.1/§21.5 and SPEC.md §10.5. */
export const WS_LIMITS = {
  /** Concurrent sockets per user; a 6th is closed with `1008`. */
  maxSocketsPerUser: 5,
  /** Inbound messages per second per socket. */
  maxInboundMessagesPerSecond: 20,
  /** Max inbound frame size, 64 KiB. */
  maxFrameBytes: 65_536,
  /** Server-side WebSocket ping frame interval. */
  serverPingIntervalMs: 30_000,
  /** Application-level `{op:'ping'}` interval the browser client uses instead. */
  clientPingIntervalMs: 25_000,
  /** No `pong` within this window means the socket is dead. */
  pongTimeoutMs: 10_000,
  /** Reconnect backoff ladder in ms, with ±20 % jitter, reset on a successful `hello`. */
  reconnectBackoffMs: [1_000, 2_000, 4_000, 8_000, 16_000, 30_000],
  /** Extra pause after a `1008` before retrying. */
  policyViolationBackoffMs: 30_000,
} as const
